import { FgdsProductType, Product } from "@domain/product/product";
import { ProductPriceData } from "@domain/product/product-price-data";
import { ProductListDiscountKey } from "@domain/project/product-list/product-list-discount-key.util";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { IsNumber, IsOptional, IsString, Min, ValidateNested } from "class-validator";

export class ProductDataPlaceholder {
  @IsString()
  public name: string;

  @ValidateNested()
  @IsInstanceOf(FgdsProductType)
  public type: FgdsProductType;

  @IsNumber()
  @Min(0, { message: "The value must not be less than 0" })
  public price: number;

  @IsString()
  @IsOptional()
  public id?: string;

  constructor(name: string, type: FgdsProductType, price: number, id?: string) {
    this.name = name;
    this.type = type;
    this.price = price;
    this.id = id;
  }
}

export class ProductData {
  public positionId?: string;
  public isIncompleteConfiguration: boolean = false;
  public discountKey?: ProductListDiscountKey;
  public discount?: number = 0;

  private constructor(
    public quantity: number,
    public name: string,
    public totalProductCost: number,
    public type: string,
    public isAvailable: boolean,
    public fullType?: string,
    public id?: string,
    public priceData?: ProductPriceData,
    public zrefPriceData?: ProductPriceData,
    public discountSlot?: ProductListDiscountSlot,
  ) {}

  static create(
    product: Product | ProductDataPlaceholder,
    localeId: string,
    currentCountry: string,
    quantity = 1,
    discountSlot?: ProductListDiscountSlot,
  ): ProductData {
    return new ProductData(
      quantity,
      product instanceof Product ? product.getLocalizedName(localeId) : product.name,
      product instanceof Product && product.getPriceDataByCountry(currentCountry)
        ? product.getPriceDataByCountry(currentCountry)!.price
        : 0,
      product.type,
      product instanceof Product ? product.isAvailable : true,
      product instanceof Product ? product.fullType : undefined,
      product.id,
      product instanceof Product && product.getPriceDataByCountry(currentCountry)
        ? product.getPriceDataByCountry(currentCountry)
        : undefined,
      product instanceof Product ? product.getZrefPriceDataByCountry(currentCountry) : undefined,
      discountSlot,
    );
  }

  static createFallback(
    partNumber: string,
    name: string,
    type: string,
    quantity = 1,
    discountSlot?: ProductListDiscountSlot,
  ): ProductData {
    return new ProductData(quantity, name, 0, type, false, undefined, partNumber, undefined, undefined, discountSlot);
  }

  static mapAttachmentsAsProductData(
    attachmentProductsWithQuantity: { product: Product; quantity: number }[],
    localeId: string,
    currentCountry: string,
    discountSlot?: ProductListDiscountSlot,
  ) {
    return attachmentProductsWithQuantity.map<ProductData>(({ product, quantity }) => {
      return ProductData.create(product, localeId, currentCountry, quantity, discountSlot);
    });
  }
}
